/**
 * BMI formulas — pure, client-safe. No data-table imports.
 * Standard (WHO): <18.5 under · 18.5–24.9 healthy · 25–29.9 over · ≥30 obese
 * Asian (WHO):    <18.5 under · 18.5–22.9 healthy · 23–27.4 over · ≥27.5 obese
 * Youth (<20):    CDC BMI-for-age percentile bands (5th/85th/95th) — we ship
 *                 a lookup approximation, sufficient for consumer directional
 *                 guidance (not clinical diagnosis).
 */

export type Cutoff = "standard" | "asian";
export type BmiCategory = "underweight" | "healthy" | "overweight" | "obese";

export function bmi(weightKg: number, heightCm: number): number {
  const m = heightCm / 100;
  if (!m || !weightKg) return 0;
  return weightKg / (m * m);
}

export function classifyAdult(bmiValue: number, cutoff: Cutoff = "standard"): BmiCategory {
  if (cutoff === "asian") {
    if (bmiValue < 18.5) return "underweight";
    if (bmiValue < 23) return "healthy";
    if (bmiValue < 27.5) return "overweight";
    return "obese";
  }
  if (bmiValue < 18.5) return "underweight";
  if (bmiValue < 25) return "healthy";
  if (bmiValue < 30) return "overweight";
  return "obese";
}

/**
 * Ideal weight range in kg for a given adult height using the target
 * BMI band for the chosen cutoff.
 */
export function idealWeightRangeKg(heightCm: number, cutoff: Cutoff = "standard"): [number, number] {
  const m = heightCm / 100;
  const [lo, hi] = cutoff === "asian" ? [18.5, 22.9] : [18.5, 24.9];
  return [Math.round(lo * m * m * 10) / 10, Math.round(hi * m * m * 10) / 10];
}

export function feetInchesToCm(feet: number, inches: number): number {
  return Math.round((feet * 30.48 + inches * 2.54) * 10) / 10;
}

export function lbsToKg(lbs: number): number {
  return Math.round(lbs * 0.45359237 * 10) / 10;
}

/**
 * CDC BMI-for-age percentile approximation. Returns an object with
 * percentile band label and target range for the reference median.
 * NOT a full CDC LMS lookup — sufficient for a directional consumer
 * signal + "consult physician for minors" framing.
 */
export function classifyYouth(bmiValue: number, ageYears: number, sex: "male" | "female" | "neutral" = "neutral") {
  // Approximate 50th percentile BMI by age (blended CDC growth chart).
  const approxMedian: Record<number, number> = {
    5: 15.3, 6: 15.3, 7: 15.5, 8: 15.9, 9: 16.4, 10: 16.9, 11: 17.5,
    12: 18.1, 13: 18.8, 14: 19.4, 15: 20.1, 16: 20.7, 17: 21.2, 18: 21.6, 19: 21.9,
  };
  const median = approxMedian[ageYears] ?? 21.9;
  const p5 = median * 0.86;
  const p85 = median * 1.15;
  const p95 = median * 1.28;
  let band: "underweight" | "healthy" | "at-risk" | "obese";
  if (bmiValue < p5) band = "underweight";
  else if (bmiValue < p85) band = "healthy";
  else if (bmiValue < p95) band = "at-risk";
  else band = "obese";
  return { band, median, p5, p85, p95, sex };
}

export const CATEGORY_LABEL: Record<BmiCategory, string> = {
  underweight: "Underweight",
  healthy: "Healthy weight",
  overweight: "Overweight",
  obese: "Obese",
};

export const CATEGORY_COLOR: Record<BmiCategory, string> = {
  underweight: "bg-sky-500",
  healthy: "bg-emerald-500",
  overweight: "bg-amber-500",
  obese: "bg-rose-500",
};